import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '../lib/queryRuntime';
import supabase from '../supabase/supabase';
import { listingService } from '../services/listingService';
import { userService } from '../services/userService';
import { chatService } from '../services/chatService';
import { reservationService } from '../services/reservationService';
import { reviewService } from '../services/reviewService';
import { fetchPagedTable } from '../services/paginatedFetch';
import { normalizeCommercialOffer, normalizeListing } from '../lib/normalizers';
import { queryKeys } from '../lib/queryClient';
import { useAuth } from './AuthContext';

const AppDataContext = createContext(null);
const PUBLIC_STALE_TIME = 2 * 60_000;

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const listingsQuery = useQuery({
    queryKey: queryKeys.listings,
    queryFn: () => listingService.getAll({ pageSize: 100, maxRows: 2000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const usersQuery = useQuery({
    queryKey: queryKeys.profiles,
    queryFn: () => userService.getPublicUsers({ pageSize: 100, maxRows: 2000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const brochuresQuery = useQuery({
    queryKey: queryKeys.brochures,
    queryFn: () => fetchPagedTable(supabase, 'brochures', { maxRows: 500 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const offersQuery = useQuery({
    queryKey: queryKeys.commercialOffers,
    queryFn: () => fetchPagedTable(supabase, 'commercial_offers', { maxRows: 1000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const providersQuery = useQuery({
    queryKey: queryKeys.providers,
    queryFn: () => fetchPagedTable(supabase, 'providers', { orderBy: 'name', ascending: true, maxRows: 1000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const mediaQuery = useQuery({
    queryKey: queryKeys.mediaAssets,
    queryFn: () => fetchPagedTable(supabase, 'media_assets', { maxRows: 1000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const adBlocksQuery = useQuery({
    queryKey: queryKeys.adBlocks,
    queryFn: () => fetchPagedTable(supabase, 'ad_blocks', { ascending: true, maxRows: 500 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const adImagesQuery = useQuery({
    queryKey: queryKeys.adImages,
    queryFn: () => fetchPagedTable(supabase, 'ad_images', { ascending: true, maxRows: 2000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const reviewsQuery = useQuery({
    queryKey: queryKeys.reviews,
    queryFn: () => reviewService.getAll({ pageSize: 100, maxRows: 5000 }),
    staleTime: PUBLIC_STALE_TIME,
  });
  const chatsQuery = useQuery({
    queryKey: queryKeys.chats(user?.id),
    queryFn: () => chatService.getMyChats(user.id),
    enabled: Boolean(user?.id),
    staleTime: 15_000,
  });
  const reservationsQuery = useQuery({
    queryKey: queryKeys.reservations(user?.id),
    queryFn: () => reservationService.getMine(user.id),
    enabled: Boolean(user?.id),
    staleTime: 30_000,
  });

  const publicResults = [
    listingsQuery, usersQuery, brochuresQuery, offersQuery, providersQuery,
    mediaQuery, adBlocksQuery, adImagesQuery, reviewsQuery,
  ];

  useEffect(() => {
    const error = publicResults.find((result) => result.error)?.error;
    if (error) console.error('Öffentliche Daten konnten nicht geladen werden:', error);
  }, [
    listingsQuery.error, usersQuery.error, brochuresQuery.error, offersQuery.error,
    providersQuery.error, mediaQuery.error, adBlocksQuery.error, adImagesQuery.error,
    reviewsQuery.error,
  ]);

  const providers = providersQuery.data || [];
  const listings = useMemo(() => (listingsQuery.data || []).map(normalizeListing), [listingsQuery.data]);
  const dbCommercialOffers = useMemo(
    () => (offersQuery.data || []).map((offer) => normalizeCommercialOffer(offer, providers)),
    [offersQuery.data, providers],
  );
  const chats = chatsQuery.data || [];
  const unreadCount = useMemo(
    () => chats.reduce((total, chat) => total + (Number(chat.unreadCount) || 0), 0),
    [chats],
  );

  const refreshData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.publicRoot }),
      user?.id ? queryClient.invalidateQueries({ queryKey: ['private-data'] }) : Promise.resolve(),
    ]);
  };

  const value = {
    listings,
    users: usersQuery.data || [],
    chats,
    brochures: brochuresQuery.data || [],
    dbCommercialOffers,
    commercialOffers: dbCommercialOffers,
    providers,
    mediaAssets: mediaQuery.data || [],
    adBlocks: adBlocksQuery.data || [],
    adImages: adImagesQuery.data || [],
    reviews: reviewsQuery.data || [],
    reservations: reservationsQuery.data || [],
    unreadCount,
    dataLoading: publicResults.some((result) => result.isPending)
      || (Boolean(user?.id) && (chatsQuery.isPending || reservationsQuery.isPending)),
    dataRefreshing: publicResults.some((result) => result.isFetching)
      || chatsQuery.isFetching || reservationsQuery.isFetching,
    refreshData,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export const useAppData = () => {
  const value = useContext(AppDataContext);
  if (!value) throw new Error('useAppData muss innerhalb des AppDataProvider verwendet werden.');
  return value;
};
